import React, { useState } from "react";
import { ArrowRight, ArrowLeft, Check, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export default function ComplianceQuestion({
  question,
  index,
  total,
  answer,
  onAnswer,
  onNext,
  onBack
}) {
  const [highlighted, setHighlighted] = useState(
    () => new Set(Array.isArray(answer) ? answer : [])
  );
  const [submitted, setSubmitted] = useState(false);

  const errorIds = question.segments
    .filter((s) => s.error)
    .map((s) => s.id);

  const isCorrect =
    errorIds.every((id) => highlighted.has(id)) &&
    [...highlighted].every((id) => errorIds.includes(id));

  const toggle = (seg) => {
    setHighlighted((prev) => {
      const next = new Set(prev);
      if (next.has(seg.id)) next.delete(seg.id);
      else next.add(seg.id);
      onAnswer(Array.from(next));
      return next;
    });
    setSubmitted(false);
  };

  return (
    <div className="max-w-xl mx-auto px-6 py-10 sm:py-16">
      <div className="flex items-center justify-between mb-8">
        <span className="text-xs font-medium tracking-widest uppercase text-primary">
          Question {index + 1} of {total}
        </span>
        <span className="text-xs text-muted-foreground">Compliance audit</span>
      </div>

      <div className="w-full h-1.5 bg-muted rounded-full mb-10 overflow-hidden">
        <div
          className="h-full bg-primary rounded-full transition-all duration-500"
          style={{ width: `${((index + 1) / total) * 100}%` }}
        />
      </div>

      <h2 className="text-xl sm:text-2xl font-semibold tracking-tight text-foreground leading-snug">
        {question.question}
      </h2>
      <p className="mt-2 text-sm text-muted-foreground">
        Click the phrases that violate compliance, then hit Submit to verify.
      </p>

      <div className="mt-6 px-5 py-5 rounded-xl border border-border bg-card text-base leading-relaxed text-foreground/90">
        {question.segments.map((seg, i) => {
          if (!seg.error) return <span key={i}>{seg.text}</span>;
          const active = highlighted.has(seg.id);
          const right = errorIds.includes(seg.id);
          return (
            <button
              key={i}
              type="button"
              onClick={() => toggle(seg)}
              className={cn(
                "inline rounded-md px-1.5 py-0.5 -mx-1 -my-0.5 transition-colors font-medium",
                !submitted &&
                  (active
                    ? "bg-primary/20 text-primary"
                    : "text-primary/80 underline decoration-dotted decoration-primary/40 hover:bg-primary/10"),
                submitted &&
                  active &&
                  right &&
                  "bg-emerald-100 text-emerald-700",
                submitted &&
                  active &&
                  !right &&
                  "bg-rose-100 text-rose-700",
                submitted &&
                  !active &&
                  right &&
                  "text-rose-600 underline decoration-dashed decoration-rose-400"
              )}
            >
              {seg.text}
            </button>
          );
        })}
      </div>

      <p className="mt-3 text-xs text-muted-foreground">
        {highlighted.size} of {errorIds.length} highlighted
      </p>

      {submitted && (
        <div
          className={cn(
            "mt-4 px-4 py-3 rounded-lg text-sm font-medium",
            isCorrect
              ? "bg-emerald-50 text-emerald-700"
              : "bg-rose-50 text-rose-700"
          )}
        >
          {isCorrect
            ? "Correct — both compliance violations identified."
            : "Not quite. Make sure both (and only the) violations are highlighted."}
        </div>
      )}

      <div className="mt-8 flex items-center justify-between">
        <Button
          variant="ghost"
          onClick={onBack}
          disabled={index === 0}
          className="text-muted-foreground hover:text-foreground disabled:opacity-30"
        >
          <ArrowLeft className="mr-2 w-4 h-4" />
          Back
        </Button>
        <div className="flex items-center gap-2">
          <Button
            onClick={() => setSubmitted(true)}
            variant="outline"
            className="rounded-full border-border"
          >
            Submit
          </Button>
          <Button
            onClick={onNext}
            className="bg-primary hover:bg-primary/90 text-primary-foreground rounded-full px-6"
          >
            {index === total - 1 ? "Finish" : "Next"}
            <ArrowRight className="ml-2 w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}